import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import {
  getArticleNameContext,
  recordNameRuling,
  type NameRulingKind,
} from '../lib/api/name-rulings.js';

interface IdentityRulingActionProps {
  articleId: string;
  /** Called after a ruling is recorded so the parent refetches the article
   *  (its verdict may have been refreshed and the article released). */
  onRuled?: () => void;
}

/** One-click resolution for an identity-check finding (B2, ruling 31).
 *
 *  The check compared the name the filing uses for itself with the name we
 *  hold. The operator answers once: the same company, or a different one.
 *  The answer is remembered for every later filing, and a same-company ruling
 *  re-verifies this entity's held articles — releasing any left with no
 *  blocker. */
export function IdentityRulingAction({ articleId, onRuled }: IdentityRulingActionProps) {
  const [rationale, setRationale] = useState('');
  const ctx = useQuery({
    queryKey: ['article-name-context', articleId],
    queryFn: () => getArticleNameContext(articleId),
    staleTime: 30_000,
  });
  const rule = useMutation({
    mutationFn: (ruling: NameRulingKind) => {
      const c = ctx.data!;
      return recordNameRuling(c.entity_id, {
        observed_name: c.filing_names[0],
        ruling,
        rationale: rationale.trim() || undefined,
        article_id: articleId,
      });
    },
    onSuccess: () => {
      ctx.refetch();
      onRuled?.();
    },
  });

  if (ctx.isLoading) {
    return <p className="text-xs text-grey-500 mt-2"><Loader2 className="inline h-3 w-3 animate-spin" /> Loading the names compared…</p>;
  }
  if (ctx.isError || !ctx.data || ctx.data.filing_names.length === 0) {
    return null; // nothing to rule on — the entity correction path still applies
  }
  const c = ctx.data;
  const filingName = c.filing_names[0];
  const live = c.live_ruling;
  // A same-company ruling already exists (e.g. seeded from Cynapse) but this
  // article still carries the finding: its verdict predates the ruling.
  const alreadySame = live?.ruling === 'same_entity';

  return (
    <div className="mt-2 rounded-md border border-grey-200 bg-grey-50 p-2 text-xs space-y-2">
      <p className="text-grey-700">
        The filing calls this company <span className="font-semibold">"{filingName}"</span>; we hold it as{' '}
        <span className="font-semibold">{c.display_name}</span>{c.code ? ` (${c.code})` : ''}.
      </p>
      {live && (
        <p className="text-grey-600">
          Ruled <span className="font-semibold">{live.ruling === 'same_entity' ? 'the same company' : live.ruling === 'different_entity' ? 'a different company' : 'a rename not yet applied'}</span>{' '}
          by {live.decided_by}{live.rationale ? ` — "${live.rationale}"` : ''}.
        </p>
      )}
      {rule.isSuccess ? (
        <p className="text-success-700">
          Ruling recorded.
          {rule.data.article
            ? ` This article: ${rule.data.article.previous_verdict} → ${rule.data.article.verdict}${rule.data.article.released ? ', released' : ''}.`
            : ''}
          {rule.data.fanout
            ? ` ${rule.data.fanout.refreshed} held article(s) re-checked, ${rule.data.fanout.released} released.`
            : ' Later filings will use it.'}
        </p>
      ) : (
        <>
          <input
            type="text"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Why (optional) — e.g. operating name of the listed entity"
            className="w-full rounded border border-grey-300 px-2 py-1 text-xs"
            aria-label="Rationale for the identity ruling"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={rule.isPending}
              onClick={() => rule.mutate('same_entity')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-success-50 text-success-700 border border-success-200 font-semibold hover:bg-success-100 disabled:opacity-50"
              title="Remember this name for this company and re-check this article and the company's recent holds. Any left with no blocker are released and auto-published."
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> {alreadySame ? 'Apply ruling to this article' : 'Same company'}
            </button>
            <button
              type="button"
              disabled={rule.isPending}
              onClick={() => rule.mutate('different_entity')}
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-danger-50 text-danger-700 border border-danger-200 font-semibold hover:bg-danger-100 disabled:opacity-50"
              title="Record that this name is a different company; the hold stands until the binding is corrected"
            >
              <XCircle className="h-3.5 w-3.5" /> Different company
            </button>
            {rule.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-grey-500 self-center" />}
          </div>
          {rule.isError && (
            <p className="text-danger-700">Could not record the ruling: {(rule.error as Error).message}</p>
          )}
        </>
      )}
    </div>
  );
}
